import type { TeamFinderFilters, TeamFinderSearchBody } from './types'

/**
 * Translate the filter form into GET /finder/teams query params. Unset filters
 * ('') are omitted so the API treats them as "any". Gender/position/province are
 * numeric ids; sport is a uid string.
 */
export const buildSearchBody = (
  filters: TeamFinderFilters
): TeamFinderSearchBody => {
  const body: TeamFinderSearchBody = {}

  // Basic-stats (generic) picks send `generic_sport_id` only — NOT `sport_id`.
  // Full-stats picks send `sport_id`. (`sportId` holds the parent generic id for
  // a generic pick, used elsewhere for positions, so it's ignored here.)
  if (filters.genericSportId) {
    body.generic_sport_id = filters.genericSportId
  } else if (filters.sportId) {
    body.sport_id = filters.sportId
  }
  if (filters.genderId) {
    body.gender_id = Number(filters.genderId)
  }
  // Sent as a comma list on the singular param — see TeamFinderSearchBody.
  // Empty ids are dropped so a stale '' can never widen to "?player_type_id=".
  const positionIds = (filters.positionIds ?? []).filter(Boolean)
  if (positionIds.length) {
    body.player_type_id = positionIds.join(',')
  }
  // Country used to be collected and never sent (SN-8317).
  if (filters.countryId) {
    body.country_id = Number(filters.countryId)
  }
  if (filters.provinceId) {
    body.province_id = Number(filters.provinceId)
  }
  // Free-text fields: trim so a whitespace-only entry is treated as unset
  // rather than sent as a param that matches nothing.
  const city = filters.city?.trim()
  if (city) {
    body.city = city
  }
  // The Team input is sent as `query` (the API's free-text search param).
  const team = filters.team?.trim()
  if (team) {
    body.query = team
  }
  // Organization is a NAME the user types, so it goes as its own free-text
  // param rather than the id-based `organization_id`.
  const organization = filters.organization?.trim()
  if (organization) {
    body.organization = organization
  }

  return body
}
